/**
 * Dev console + production error reporting.
 *
 * In development everything routes to the native console so debugging
 * is unchanged. In every environment `logger.error(...)` is ALSO sent
 * to the backend `/api/client-errors` collection so admins can see
 * what's breaking for staff in the field, without us needing a 3rd
 * party service like Sentry.
 *
 * Design notes
 * ------------
 *  • We queue + debounce (2s) so a burst of errors doesn't hammer the
 *    server, and use `fetch keepalive: true` so errors fired during
 *    page-unload (the worst kind to debug) still make it through.
 *  • Only `logger.error` is sent — `warn / log / info / debug` are
 *    dev-console only. Production noise is kept to true errors.
 *  • Auth token is read from localStorage; if missing we just skip
 *    the network call (don't break the page).
 *  • A simple in-memory ring buffer caps the queue at 50 entries so a
 *    runaway error loop can't OOM the tab.
 */
const isDev = process.env.NODE_ENV !== 'production';
const API = process.env.REACT_APP_BACKEND_URL;

// In-memory queue. We flush every 2s OR when 5 errors stack up.
const QUEUE_LIMIT = 50;
const FLUSH_AFTER_N = 5;
const FLUSH_DEBOUNCE_MS = 2000;
let queue = [];
let flushTimer = null;

function snapshotContext() {
  // Lightweight context we attach to every error. Kept tiny so we don't
  // bloat each insert with megabytes of state.
  try {
    return {
      url: typeof window !== 'undefined' ? window.location.href : '',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    };
  } catch {
    return { url: '', user_agent: '' };
  }
}

function getAuthToken() {
  try {
    return typeof localStorage !== 'undefined' ? (localStorage.getItem('heda_token') || '') : '';
  } catch {
    return '';
  }
}

function stringifyArg(arg) {
  if (arg == null) return String(arg);
  if (arg instanceof Error) return `${arg.name}: ${arg.message}\n${arg.stack || ''}`;
  if (typeof arg === 'string') return arg;
  try { return JSON.stringify(arg).slice(0, 2000); } catch { return String(arg); }
}

function buildErrorRecord(args) {
  // First Error instance in the args wins for `stack`; everything else
  // is joined into `message`. This matches how console.error is
  // typically called: `logger.error('Failed to do X:', err)`.
  let stack = '';
  const parts = [];
  for (const a of args) {
    if (!stack && a instanceof Error) stack = a.stack || `${a.name}: ${a.message}`;
    parts.push(stringifyArg(a));
  }
  const ctx = snapshotContext();
  return {
    level: 'error',
    message: parts.join(' ').slice(0, 2000),
    stack: stack.slice(0, 8000),
    url: ctx.url,
    user_agent: ctx.user_agent,
    extra: {},
  };
}

function flush() {
  flushTimer = null;
  if (queue.length === 0) return;
  if (!API) return;
  const token = getAuthToken();
  if (!token) {
    // No auth — we silently drop the queue. The user isn't logged in
    // yet so the backend wouldn't accept the POST anyway.
    queue = [];
    return;
  }
  const batch = queue;
  queue = [];
  // Fire each error as its own POST so the backend's de-dup logic per
  // fingerprint applies cleanly. Use keepalive so unloaded tabs still
  // send. We intentionally do NOT await — fire-and-forget.
  for (const rec of batch) {
    try {
      fetch(`${API}/api/client-errors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(rec),
        keepalive: true,
      }).catch(() => { /* never let reporting errors break the app */ });
    } catch { /* never let reporting errors break the app */ }
  }
}

function scheduleFlush() {
  if (queue.length >= FLUSH_AFTER_N) {
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    flush();
    return;
  }
  if (flushTimer) return;
  flushTimer = setTimeout(flush, FLUSH_DEBOUNCE_MS);
}

function reportError(args) {
  if (queue.length >= QUEUE_LIMIT) {
    // Drop oldest to make room — runaway error loop protection.
    queue.shift();
  }
  queue.push(buildErrorRecord(args));
  scheduleFlush();
}

// Best-effort flush on page unload so we don't lose the last error.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flush);
  // Catch any unhandled errors / promise rejections globally — these
  // are the most useful ones to surface for the admin.
  window.addEventListener('error', (e) => {
    if (!e || !e.error) return;
    reportError([`Uncaught error:`, e.error]);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e?.reason;
    if (!reason) return;
    reportError([`Unhandled promise rejection:`, reason]);
  });
}

export const logger = {
  log: (...args) => { if (isDev) console.log(...args); },
  info: (...args) => { if (isDev) console.info(...args); },
  warn: (...args) => { if (isDev) console.warn(...args); },
  error: (...args) => {
    if (isDev) console.error(...args);
    // Always send to backend — production visibility is the point.
    reportError(args);
  },
  debug: (...args) => { if (isDev) console.debug(...args); },
};

export default logger;
